import { useEffect, useState } from "react";
import { EditorContent, useEditor, type Editor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import { Markdown } from "tiptap-markdown";
import EmojiPicker, { type EmojiClickData } from "emoji-picker-react";
import { Bold, Code, Italic, Link2, List, ListOrdered, Paperclip, SmilePlus } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Separator } from "@/components/ui/separator";
import { normalizeEditorMarkdown } from "@/lib/markdown-utils";
import { cn } from "@/lib/utils";

// The editor's value IS markdown: announcement bodies are rendered with
// react-markdown in the portal, passed as mrkdwn to Slack, and converted
// for email. The toolbar mirrors the announcements composer in the main
// app's settings (bold/italic, lists, link, code block, emoji).
interface RichTextEditorProps {
  value: string;
  onChange: (markdown: string) => void;
  placeholder?: string;
  onFileSelect?: () => void;
}

function ToolbarButton({
  editor,
  active,
  onClick,
  label,
  children,
}: {
  editor: Editor;
  active: boolean;
  onClick: () => void;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <Button
      type="button"
      variant={active ? "secondary" : "ghost"}
      size="sm"
      className="size-7 p-0"
      aria-label={label}
      title={label}
      disabled={!editor.isEditable}
      onMouseDown={(e) => {
        // Keep the selection so the mark applies to the highlighted text.
        e.preventDefault();
      }}
      onClick={onClick}
    >
      {children}
    </Button>
  );
}

export function RichTextEditor({ value, onChange, placeholder, onFileSelect }: RichTextEditorProps) {
  const [emojiOpen, setEmojiOpen] = useState(false);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: false,
        blockquote: false,
        horizontalRule: false,
      }),
      Link.configure({ openOnClick: false }),
      Markdown.configure({ transformPastedText: true }),
    ],
    content: value,
    editorProps: {
      attributes: {
        class: cn(
          "min-h-32 w-full rounded-b-md px-3 py-2 text-sm focus:outline-none",
          "[&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5",
          "[&_a]:text-primary [&_a]:underline",
          "[&_pre]:my-1 [&_pre]:rounded-md [&_pre]:bg-muted [&_pre]:p-2 [&_pre]:font-mono [&_pre]:text-xs",
          "[&_code]:rounded [&_code]:bg-muted [&_code]:px-1 [&_code]:font-mono [&_code]:text-xs",
        ),
      },
    },
    onUpdate: ({ editor: e }) => {
      const markdown = (e.storage.markdown as { getMarkdown: () => string }).getMarkdown();
      onChange(e.isEmpty ? "" : normalizeEditorMarkdown(markdown));
    },
  });

  // External resets (e.g. "New broadcast" clearing the form) must reflect
  // in the editor; normal typing round-trips through onUpdate and is a no-op.
  useEffect(() => {
    if (!editor) return;
    // Compare against the SAME shape onChange emits: without normalizing
    // here, every Shift+Enter or typed ">" would look like an external
    // change and reset the content (losing the cursor) on the next render.
    const current = normalizeEditorMarkdown(
      (editor.storage.markdown as { getMarkdown: () => string }).getMarkdown(),
    );
    if (value !== current && !(value === "" && editor.isEmpty)) {
      editor.commands.setContent(value);
    }
  }, [editor, value]);

  // The modal emoji popover can leave a stale pointer-events lock on
  // <body> if the whole dialog unmounts while it is open.
  useEffect(
    () => () => {
      document.body.style.pointerEvents = "";
    },
    [],
  );

  if (!editor) return null;

  const setLink = () => {
    const previous = (editor.getAttributes("link") as { href?: string }).href;
    const url = window.prompt("Link URL", previous ?? "https://");
    if (url === null) return;
    if (url === "" || url === "https://") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  };

  const insertEmoji = (emojiData: EmojiClickData) => {
    editor.chain().focus().insertContent(emojiData.emoji).run();
    setEmojiOpen(false);
  };

  return (
    <div className="rounded-md border border-input bg-transparent shadow-xs focus-within:border-ring focus-within:ring-[3px] focus-within:ring-ring/50">
      <div className="flex items-center gap-0.5 border-b border-input px-1.5 py-1">
        <ToolbarButton
          editor={editor}
          active={editor.isActive("bold")}
          onClick={() => editor.chain().focus().toggleBold().run()}
          label="Bold"
        >
          <Bold className="size-3.5" />
        </ToolbarButton>
        <ToolbarButton
          editor={editor}
          active={editor.isActive("italic")}
          onClick={() => editor.chain().focus().toggleItalic().run()}
          label="Italic"
        >
          <Italic className="size-3.5" />
        </ToolbarButton>
        <Separator orientation="vertical" className="mx-0.5 h-4" />
        <ToolbarButton
          editor={editor}
          active={editor.isActive("bulletList")}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          label="Bullet list"
        >
          <List className="size-3.5" />
        </ToolbarButton>
        <ToolbarButton
          editor={editor}
          active={editor.isActive("orderedList")}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          label="Numbered list"
        >
          <ListOrdered className="size-3.5" />
        </ToolbarButton>
        <Separator orientation="vertical" className="mx-0.5 h-4" />
        <ToolbarButton editor={editor} active={editor.isActive("link")} onClick={setLink} label="Link">
          <Link2 className="size-3.5" />
        </ToolbarButton>
        {onFileSelect && (
          <ToolbarButton editor={editor} active={false} onClick={onFileSelect} label="Attach files">
            <Paperclip className="size-3.5" />
          </ToolbarButton>
        )}
        <ToolbarButton
          editor={editor}
          active={editor.isActive("codeBlock")}
          onClick={() => editor.chain().focus().toggleCodeBlock().run()}
          label="Code block"
        >
          <Code className="size-3.5" />
        </ToolbarButton>
        <Popover modal open={emojiOpen} onOpenChange={setEmojiOpen}>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant={emojiOpen ? "secondary" : "ghost"}
              size="sm"
              className="size-7 p-0"
              aria-label="Emoji"
              title="Emoji"
            >
              <SmilePlus className="size-3.5" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <EmojiPicker onEmojiClick={insertEmoji} lazyLoadEmojis />
          </PopoverContent>
        </Popover>
      </div>
      <div className="relative">
        {editor.isEmpty && placeholder && (
          <p className="pointer-events-none absolute top-2 left-3 text-sm text-muted-foreground">{placeholder}</p>
        )}
        <EditorContent editor={editor} />
      </div>
    </div>
  );
}
